import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { getProducts, getCategories } from '../lib/api';
import { trackSearch } from '../lib/analytics';
import { Search as SearchIcon } from 'lucide-react';
import logger from '../lib/logger';

const SORTS = [
  { value: 'default', label: 'Default sorting' },
  { value: 'latest', label: 'Sort by latest' },
  { value: 'price-asc', label: 'Price: low to high' },
  { value: 'price-desc', label: 'Price: high to low' },
  { value: 'name-asc', label: 'Name: A to Z' },
];

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCat = searchParams.get('category') || 'all';
  const initialSearch = searchParams.get('search') || '';

  const [category, setCategory] = useState(initialCat);
  const [sort, setSort] = useState('default');
  const [search, setSearch] = useState(initialSearch);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCategories().then(setCategories).catch(() => {});
    getProducts().then((d) => setTotalCount(d.length)).catch(() => {});
  }, []);

  useEffect(() => {
    setCategory(searchParams.get('category') || 'all');
    setSearch(searchParams.get('search') || '');
  }, [searchParams]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category !== 'all') params.category = category;
    if (search) params.search = search;
    if (sort !== 'default') params.sort = sort;
    getProducts(params)
      .then(setProducts)
      .catch((e) => logger.error(e))
      .finally(() => setLoading(false));
    if (search && search.length >= 2) {
      const t = setTimeout(() => trackSearch(search), 800);
      return () => clearTimeout(t);
    }
  }, [category, sort, search]);

  const updateCategory = (cat) => {
    setCategory(cat);
    const next = new URLSearchParams(searchParams);
    if (cat === 'all') next.delete('category');
    else next.set('category', cat);
    setSearchParams(next);
  };

  const onSearchChange = (val) => {
    setSearch(val);
    const next = new URLSearchParams(searchParams);
    if (val) next.set('search', val);
    else next.delete('search');
    setSearchParams(next);
  };

  const totalForCat = useMemo(() => totalCount, [totalCount]);

  return (
    <div className="bg-black text-white min-h-screen">
      <div className="border-b border-zinc-900 py-12">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold">Shop</h1>
          <p className="text-zinc-400 mt-3">
            Browse research-grade peptides. All ≥99% purity with third-party COAs.
          </p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-10 grid lg:grid-cols-[260px_1fr] gap-10">
        <aside className="space-y-8">
          <div>
            <h3 className="text-white font-semibold mb-3 uppercase tracking-wide text-sm">Search</h3>
            <div className="relative">
              <SearchIcon size={16} className="absolute top-1/2 left-3 -translate-y-1/2 text-zinc-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search products..."
                className="w-full bg-zinc-950 border border-zinc-800 text-white pl-9 pr-3 py-2 rounded text-sm placeholder-zinc-600 focus:outline-none focus:border-[#909090]"
              />
            </div>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-3 uppercase tracking-wide text-sm">Categories</h3>
            <ul className="space-y-1.5">
              <li>
                <button
                  onClick={() => updateCategory('all')}
                  className={`text-sm text-left w-full py-1.5 transition-colors ${category === 'all' ? 'text-[#909090] font-semibold' : 'text-zinc-400 hover:text-white'}`}
                >
                  All Products ({totalForCat})
                </button>
              </li>
              {categories.map((c) => (
                <li key={c.slug}>
                  <button
                    onClick={() => updateCategory(c.name)}
                    className={`text-sm text-left w-full py-1.5 transition-colors ${category === c.name ? 'text-[#909090] font-semibold' : 'text-zinc-400 hover:text-white'}`}
                  >
                    {c.name} ({c.count})
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-zinc-950 border border-zinc-900 p-4 rounded">
            <p className="text-xs text-zinc-400 leading-relaxed">
              All Hydro Health products are independently lab-tested. COAs are available on request.
            </p>
          </div>
        </aside>

        <main>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <p className="text-zinc-400 text-sm">
              Showing {products.length} {category === 'all' && !search ? `of ${totalForCat}` : ''} results
            </p>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-zinc-950 border border-zinc-800 text-white text-sm px-3 py-2 rounded focus:outline-none focus:border-[#909090]"
            >
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={`skel-${i}`} className="aspect-square bg-zinc-950 border border-zinc-900 rounded animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="py-20 text-center text-zinc-500">No products match your search.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ShopPage;
